import { createContext, Fragment, useEffect, useMemo, useState } from 'react';
import { Page } from '../theme/elements/Page';
import { Feature } from 'components/Feature';
import { emailFields, emailFieldsInitial } from 'constants/ui.constants';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import {
	getCategory,
	getCurrentTab,
	getError,
	getPages,
	getSelectedLanguage,
	setError,
} from 'state/reducers/content.reducers';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { WarningDialog } from 'components/WarningDialog';
import { sendEmailExample } from 'services/content.service';
import { IFieldGroup, ISection } from 'constants/content.constants';

export const isSendExampleEmailContext = createContext({
	isSendExampleEmail: false,
	handleSendExampleEmail: () => {},
	onChangeEmailFields: (itemId: number, text: string) => {},
	emailFields: emailFieldsInitial,
});

export const ContentApp = () => {
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const contentError = useAppSelector(getError);
	const category = useAppSelector(getCategory);
	const [emailFields, setEmailFields] = useState<emailFields>(emailFieldsInitial);
	const isSendExampleEmail = useMemo(() => category === 'email', [category]);
	const pages = useAppSelector(getPages);
	const currentTab = useAppSelector(getCurrentTab);
	const language = useAppSelector(getSelectedLanguage);
	const currentCategory = useAppSelector(getCategory);

	const handleSendExampleEmail = async () => {
		dispatch(sendEmailExample(emailFields));
	};

	useEffect(() => setEmailFields(emailFieldsInitial), [currentCategory]);

	const currentEmailFields = useMemo(() => {
		if (isSendExampleEmail) {
			const page = pages[currentTab];

			const currenFieldToUse: any[] = [];
			page?.sections?.forEach((section: ISection) => {
				section.fieldGroups?.forEach((fieldGroup: IFieldGroup) => {
					fieldGroup.fieldGroupItems?.forEach((fieldGroupItem: any) => {
						currenFieldToUse.push(fieldGroupItem);
					});
				});
			});
			return currenFieldToUse;
		}
		return [];
	}, [isSendExampleEmail, pages, currentTab]);

	const onChangeEmailFields = (itemId: number, text: string) => {
		const item = currentEmailFields.find((it: any) => it.id === itemId);
		if (!item) {
			setEmailFields({ ...emailFields, email: text });
		} else {
			if (item.key.endsWith('header')) {
				setEmailFields({ ...emailFields, header: text });
			} else if (item.key.endsWith('content')) {
				setEmailFields({ ...emailFields, content: text });
			} else if (item.key.endsWith('link')) {
				setEmailFields({ ...emailFields, link: text });
			}
		}
	};

	useEffect(() => {
		let emailFieldsTmp: any = { email: '', content: '', header: '', link: '' };

		currentEmailFields.forEach((item: any) => {
			if (item.key.endsWith('header')) {
				emailFieldsTmp = { ...emailFieldsTmp, header: item[language?.code ?? 'he'] };
			} else if (item.key.endsWith('content')) {
				emailFieldsTmp = { ...emailFieldsTmp, content: item[language?.code ?? 'he'] };
			} else if (item.key.endsWith('link')) {
				emailFieldsTmp = { ...emailFieldsTmp, link: item[language?.code ?? 'he'] };
			}
		});
		setEmailFields(emailFieldsTmp);
	}, [currentEmailFields, language]);

	const openDialog = useMemo(() => {
		return contentError?.exist;
	}, [contentError]);

	return (
		<Fragment>
			<WarningDialog
				open={openDialog}
				subTitle={contentError?.text}
				handleCancel={() => {
					dispatch(setError({ exist: false }));
				}}
			/>
			<Page>
				<isSendExampleEmailContext.Provider
					value={{ handleSendExampleEmail, onChangeEmailFields, isSendExampleEmail, emailFields }}
				>
					<Feature />
				</isSendExampleEmailContext.Provider>
			</Page>
		</Fragment>
	);
};
